/* eslint-disable filename-rules/match -- versioned style-config files are named after the DLS version */
import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { dls } from '@joinrepublic/dls-ui-core';

/**
 * DLS 2.2 Heading — the numeric `title-*` scale.
 *
 * Each size **reads** its `textStyle` rather than restating the metrics. The 2.1 config below
 * restates them, and the wallet theme holds a byte-identical copy of both, which is how
 * `textStyles['body-sm-md']` and `Text.sizes['body-sm-md']` already drifted apart. Spreading the
 * token means `size="title-400"` and `textStyle="title-400"` cannot disagree.
 *
 * Not `textStyle: 'title-400'` as a key here: the designer's prototype documents that resolving a
 * text style from inside a component recipe let the component's own size styles back in. The
 * spread is the same length and cannot do that.
 *
 * Weight is a prop of the scale, not part of a style name, so it is applied here: `title` is
 * semibold (600) throughout except `title-900`, the one step the spec sets in Medium (500).
 * Figma's "Bold" means 600 — 700 is a weight Inter Republic cannot render.
 */
const step = (
  name: keyof typeof dls.textStyles,
  fontWeight: 'semibold' | 'medium' = 'semibold'
) => ({ ...dls.textStyles[name], fontWeight });

export const styleConfig: ComponentStyleConfig = {
  baseStyle: {
    color: 'neutral.fg-primary',
  },
  sizes: {
    'title-50': step('title-50'),
    'title-100': step('title-100'),
    'title-200': step('title-200'),
    'title-300': step('title-300'),
    'title-400': step('title-400'),
    'title-500': step('title-500'),
    'title-600': step('title-600'),
    'title-700': step('title-700'),
    'title-800': step('title-800'),
    'title-900': step('title-900', 'medium'),
  },
  variants: {
    'text-primary': {
      color: 'neutral.fg-primary',
    },
    'text-secondary': {
      color: 'neutral.fg-secondary',
    },
    'text-tertiary': {
      color: 'neutral.fg-tertiary',
    },
    'text-disabled': {
      color: 'neutral.fg-disabled',
    },
  },
  defaultProps: {
    size: 'title-600',
    variant: 'text-primary',
  },
};
